const DEFAULT_SUPABASE_URL = "https://api.fork.sa";
const DEFAULT_PORTAL_ORIGINS = {
  hire: "https://hire.fork.sa",
  seek: "https://seek.fork.sa",
  admin: "https://admin.fork.sa",
} as const;
const DEFAULT_FORK_HOME_URL = "https://fork.sa";
type Portal = "hire" | "seek" | "admin";
type ResetPasswordPortal = "hire" | "seek";

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, "");
}

function getCurrentUrl(url?: string | URL) {
  if (url instanceof URL) return new URL(url.toString());
  return new URL(url ?? window.location.href, window.location.origin);
}

function assertResetPasswordPortal(portal: unknown): asserts portal is ResetPasswordPortal {
  if (portal !== "hire" && portal !== "seek") {
    throw new Error("reset_password_portal_required");
  }
}

export function getSupabaseBaseUrl() {
  const configuredUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
  return trimTrailingSlash(configuredUrl || DEFAULT_SUPABASE_URL);
}

export function getSupabasePublishableKey() {
  return import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() || "";
}

export function buildFunctionUrl(
  functionName: string,
  query?: Record<string, string | number | boolean | null | undefined>,
) {
  const url = new URL(`/functions/v1/${functionName}`, `${getSupabaseBaseUrl()}/`);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

export function getPortalOrigin(portal: Portal) {
  const envKey = `VITE_PUBLIC_${portal.toUpperCase()}_URL` as const;
  const configuredOrigin = import.meta.env[envKey]?.trim();
  return trimTrailingSlash(configuredOrigin || DEFAULT_PORTAL_ORIGINS[portal]);
}

export function buildPublicJobApplicationUrl(
  publicSlug: string,
  publicHireOrigin = getPortalOrigin("hire"),
) {
  const slug = publicSlug.trim();
  if (!slug) throw new Error("public_job_slug_required");
  return new URL(`/apply/${encodeURIComponent(slug)}`, `${trimTrailingSlash(publicHireOrigin)}/`).toString();
}

export function buildForkSeekPostApplicationUrl(
  isAuthenticated: boolean,
  publicSeekOrigin = getPortalOrigin("seek"),
) {
  const path = isAuthenticated ? "/seek/applications" : "/seek/signup";
  return new URL(path, `${trimTrailingSlash(publicSeekOrigin)}/`).toString();
}

export function getForkHomeUrl() {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    return window.location.origin;
  }

  const configuredUrl = import.meta.env.VITE_PUBLIC_FORK_URL?.trim();
  return trimTrailingSlash(configuredUrl || DEFAULT_FORK_HOME_URL);
}

export function resolveResetPasswordPortalFromUrl(url?: string | URL): ResetPasswordPortal | null {
  const currentUrl = getCurrentUrl(url);
  const pathname = currentUrl.pathname.toLowerCase();

  if (pathname === "/seek/reset-password" || pathname.startsWith("/seek/reset-password/")) {
    return "seek";
  }

  if (pathname === "/hire/reset-password" || pathname.startsWith("/hire/reset-password/")) {
    return "hire";
  }

  const portalParam = currentUrl.searchParams.get("portal");
  if (portalParam === "seek" || portalParam === "hire") return portalParam;

  const hostname = currentUrl.hostname.toLowerCase();
  if (hostname === "seek.fork.sa") return "seek";
  if (hostname === "hire.fork.sa") return "hire";

  return null;
}

export function getResetPasswordPath(portal: ResetPasswordPortal) {
  assertResetPasswordPortal(portal);
  return portal === "seek" ? "/seek/reset-password" : "/hire/reset-password";
}

export function getCurrentPortalAuthPath(url?: string | URL) {
  const currentUrl = getCurrentUrl(url);
  const pathname = currentUrl.pathname.toLowerCase();

  if (pathname.startsWith("/seek/")) return "/seek/auth";
  if (pathname.startsWith("/admin/") || pathname.startsWith("/super-admin/")) return "/admin/auth";
  if (pathname.startsWith("/hire/")) return "/hire/auth";

  const portalParam = currentUrl.searchParams.get("portal");
  if (portalParam === "seek") return "/seek/auth";
  if (portalParam === "admin") return "/admin/auth";
  if (portalParam === "hire") return "/hire/auth";

  const resetPortal = resolveResetPasswordPortalFromUrl(currentUrl);
  if (resetPortal === "seek") return "/seek/auth";
  if (resetPortal === "hire") return "/hire/auth";

  const hostname = currentUrl.hostname.toLowerCase();

  if (hostname === "seek.fork.sa") return "/seek/auth";
  if (hostname === "admin.fork.sa") return "/admin/auth";
  return "/hire/auth";
}

export function getResetPasswordRedirectUrl(portal: ResetPasswordPortal) {
  assertResetPasswordPortal(portal);
  return new URL(getResetPasswordPath(portal), `${getPortalOrigin(portal)}/`).toString();
}

export function getVerifyEmailRedirectUrl(portal: "hire" | "seek") {
  const pathname = portal === "hire" ? "/hire/verify-email" : "/seek/verify-email";
  return new URL(pathname, `${getPortalOrigin(portal)}/`).toString();
}

export function getEmailChangeRedirectUrl(portal: "hire" | "seek") {
  const pathname = portal === "hire" ? "/hire/verify-email-change" : "/seek/verify-email-change";
  return new URL(pathname, `${getPortalOrigin(portal)}/`).toString();
}
